import { useMutation, useQuery } from '@apollo/client';
import { Layout } from '@components/Layout';
import { GET_BRAND, UPDATE_BRAND } from '@gql/brand';
import { GET_OPTIONS_TALENT } from '@gql/options';
import { Box, Button, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import BrandForm from './BrandForm';
import { BrandFormValues, BrandItem } from './types';
import { toUploadFileInput, toUploadFilesInput } from './utils';

const BrandEdit = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();

    const formMethods = useForm<BrandFormValues>({
        mode: 'onChange',
        defaultValues: {
            sector: '',
            brandName: '',
            description: '',
            proId: '',
            isFeatured: false,
            brandLogo: null,
            brandBanner: null,
            brandPhotos: [],
            brandVideos: [],
        },
    });

    const { data: optionsData } = useQuery(GET_OPTIONS_TALENT);
    const { data: brandData, loading: loadingBrand, error: brandError } = useQuery(GET_BRAND, {
        variables: { id },
        skip: !id,
    });
    const [updateBrand, { loading: isUpdating }] = useMutation(UPDATE_BRAND);

    const brand: BrandItem | undefined = brandData?.getBrand;

    useEffect(() => {
        if (brand) {
            formMethods.reset({
                sector: brand.sector?._id ?? '',
                brandName: brand.brandName ?? '',
                description: brand.description ?? '',
                proId: brand.pro?._id ?? '',
                isFeatured: Boolean(brand.isFeatured),
                brandLogo: brand.brandLogo?._id
                    ? {
                        _id: brand.brandLogo._id,
                        url: brand.brandLogo.url,
                        name: brand.brandLogo.name,
                        size: brand.brandLogo.size,
                        mimetype: brand.brandLogo.mimetype,
                    }
                    : null,
                brandBanner: brand.brandBanner?._id
                    ? {
                        _id: brand.brandBanner._id,
                        url: brand.brandBanner.url,
                        name: brand.brandBanner.name,
                        size: brand.brandBanner.size,
                        mimetype: brand.brandBanner.mimetype,
                    }
                    : null,
                brandPhotos: (brand.brandPhotos as any) ?? [],
                brandVideos: (brand.brandVideos as any) ?? [],
            });
        }
    }, [brand, formMethods]);

    const handleSubmit = async (values: BrandFormValues) => {
        if (!id) return;

        const brandInput: any = {
            brandName: values.brandName,
            sector: values.sector || undefined,
            description: values.description,
            isFeatured: values.isFeatured,
            brandLogo: toUploadFileInput(values.brandLogo),
            brandBanner: toUploadFileInput(values.brandBanner),
            brandPhotos: toUploadFilesInput(values.brandPhotos),
            brandVideos: toUploadFilesInput(values.brandVideos),
        };

        try {
            await updateBrand({
                variables: {
                    id,
                    brandInput,
                },
                context: {
                    fetchOptions: {
                        useUpload: true,
                    },
                },
            });
            showSnackbar('Marque mise à jour avec succès.', 'success');
            navigate(`/brands/${id}/detail`);
        } catch (err: any) {
            showSnackbar(err?.message || "Échec de l'enregistrement de la marque.", 'error');
        }
    };

    const handleCancel = () => {
        navigate(`/brands/${id}/detail`);
    };

    const breadcrumbs = [
        { label: 'Tableau de bord', href: '/', value: 'dashboard' },
        { label: 'Gestion des marques', href: '/brands', value: 'brands' },
        { label: 'Modifier la marque', href: `/brands/${id}/edit`, value: 'edit' },
    ];

    if (loadingBrand) {
        return (
            <Layout heading="Modifier la marque" breadcrumbs={breadcrumbs}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Layout>
        );
    }

    if (brandError || !brand) {
        return (
            <Layout heading="Modifier la marque" breadcrumbs={breadcrumbs}>
                <Box p={3}>
                    <Card sx={{ maxWidth: 900, mx: 'auto' }}>
                        <CardContent sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="h6" color="error" gutterBottom>
                                Erreur lors du chargement de la marque
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={3}>
                                {brandError?.message || 'Marque introuvable'}
                            </Typography>
                            <Button variant="contained" onClick={() => navigate('/brands')}>
                                Retour à la liste
                            </Button>
                        </CardContent>
                    </Card>
                </Box>
            </Layout>
        );
    }

    return (
        <Layout heading="Modifier la marque" breadcrumbs={breadcrumbs}>
            <Box p={3}>
                <Card sx={{ maxWidth: 900, mx: 'auto' }}>
                    <CardContent sx={{ p: 4 }}>
                        <FormProvider {...formMethods}>
                            <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
                                <BrandForm
                                    formMode="edit"
                                    editingBrand={brand}
                                    optionsData={optionsData}
                                />
                                <Stack direction="row" spacing={2} mt={4} justifyContent="flex-end">
                                    <Button
                                        variant="text"
                                        color="secondary"
                                        onClick={handleCancel}
                                        disabled={isUpdating}
                                    >
                                        Annuler
                                    </Button>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        disabled={isUpdating}
                                    >
                                        {isUpdating ? 'Mise à jour...' : 'Mettre à jour la marque'}
                                    </Button>
                                </Stack>
                            </form>
                        </FormProvider>
                    </CardContent>
                </Card>
            </Box>
        </Layout>
    );
};

export default BrandEdit;
